// * Используем useLocalStorage из react-use в качестве основы
// * копия https://github.com/streamich/react-use/blob/master/src/useLocalStorage.ts
// * но поправили баг на https://github.com/streamich/react-use/blob/master/src/useLocalStorage.ts#L82
// * не хватает зависимости от [state]
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { identity } from 'lodash-es';

type parserOptions<T> =
  | {
      raw: true;
    }
  | {
      raw: false;
      serializer: (value: T) => string;
      deserializer: (value: string) => T;
    };

export const useLocalStorage = <T>(
  key: string,
  initialValue?: T,
  options?: parserOptions<T>,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] => {
  if (!key) {
    throw new Error('useLocalStorage key may not be falsy');
  }

  const deserializer = useMemo(() => {
    if (!options) return JSON.parse;

    return options.raw ? identity : options.deserializer;
  }, [options]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const initializer = useRef((innerKey: string) => {
    try {
      let serializer;
      if (!options) {
        serializer = JSON.stringify;
      } else {
        serializer = options.raw ? String : options.serializer;
      }

      const localStorageValue = localStorage.getItem(innerKey);
      if (localStorageValue !== null) {
        return deserializer(localStorageValue) as T;
      }
      if (initialValue) {
        localStorage.setItem(innerKey, serializer(initialValue));
      }
      return initialValue;
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw. JSON.parse and JSON.stringify
      // can throw, too.
      return initialValue;
    }
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [state, setState] = useState<T | undefined>(() =>
    initializer.current(key),
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => setState(initializer.current(key)), [key]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const set: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (valOrFunc) => {
      setState((prevState) => {
        try {
          const newState =
            typeof valOrFunc === 'function'
              ? // eslint-disable-next-line @typescript-eslint/ban-types
                ((valOrFunc as Function)(prevState) as T)
              : valOrFunc;
          if (typeof newState === 'undefined') return prevState;
          let value: string;

          if (options)
            if (options.raw)
              if (typeof newState === 'string') value = newState;
              else value = JSON.stringify(newState);
            else if (options.serializer) value = options.serializer(newState);
            else value = JSON.stringify(newState);
          else value = JSON.stringify(newState);

          localStorage.setItem(key, value);
          return deserializer(value) as T;
        } catch {
          // If user is in private mode or has storage restriction
          // localStorage can throw. Also JSON.stringify can throw.
          return prevState;
        }
      });
    },
    [key, setState, deserializer, options],
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
      setState(undefined);
    } catch {
      // If user is in private mode or has storage restriction
      // localStorage can throw.
    }
  }, [key, setState]);

  return [state, set, remove];
};
